import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { SelectVolunteersDialog } from '../../../common/select-volunteers/SelectVolunteersDialog';
import { GetSelectVolunteerFragment } from '../../../common/select-volunteers/__generated__/GetSelectVolunteerFragment';
import { FallbackAvatar } from '../user-profile/components/Avatar';
import { useSession } from '../../../global/auth/useSession';
import { useApiUrl } from '../../../global/config/useApiUrl';

interface CreateChannelDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateChannelDialog = ({ open, onClose, onCreated }: CreateChannelDialogProps) => {
  const { user } = useSession();
  const apiUrl = useApiUrl();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedVolunteers, setSelectedVolunteers] = useState<GetSelectVolunteerFragment[]>([]);
  const [showVolunteerDialog, setShowVolunteerDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAddVolunteers = (volunteers: GetSelectVolunteerFragment[]) => {
    setSelectedVolunteers((prev) => {
      const existingIds = new Set(prev.map((v) => v.volunteerId));
      const newVols = volunteers.filter((v) => !existingIds.has(v.volunteerId));
      return [...prev, ...newVols];
    });
    setShowVolunteerDialog(false);
  };

  const handleRemoveVolunteer = (volunteerId: string) => {
    setSelectedVolunteers((prev) => prev.filter((v) => v.volunteerId !== volunteerId));
  };

  const handleSubmit = async () => {
    if (!name.trim() || !user?.token) return;

    setSubmitting(true);
    try {
      await fetch(`${apiUrl}/vm/chat/channel/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `JWT ${user.token}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          description: description.trim() || null,
          volunteerIds: selectedVolunteers.map((v) => v.volunteerId),
        }),
      });
      onCreated();
      handleClose();
    } catch (err) {
      console.error('Failed to create channel:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setSelectedVolunteers([]);
    onClose();
  };

  const getInitials = (vol: GetSelectVolunteerFragment) =>
    `${vol.profile.preferredName[0]}${vol.profile.lastName[0]}`.toUpperCase();

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Grid container justify='space-between' alignItems='flex-start'>
            <Grid item>
              <Typography variant='h6'>Create Channel</Typography>
              <Typography variant='body2' color='textSecondary'>
                Create a new chat channel and add members
              </Typography>
            </Grid>
            <Grid item>
              <IconButton aria-label='Close' color='inherit' onClick={handleClose}>
                <CloseIcon color='action' />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Channel Name'
                variant='outlined'
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Description (optional)'
                variant='outlined'
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                maxRows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                <Typography variant='subtitle2'>Members</Typography>
                <TabletButton variant='text' color='primary' onClick={() => setShowVolunteerDialog(true)}>
                  Add Volunteers
                </TabletButton>
              </Box>

              {selectedVolunteers.length === 0 ? (
                <Typography variant='body2' color='textSecondary' style={{ textAlign: 'center', padding: 16 }}>
                  No volunteers added yet
                </Typography>
              ) : (
                <Box display='flex' flexWrap='wrap' style={{ gap: 8 }}>
                  {selectedVolunteers.map((vol) => (
                    <Chip
                      key={vol.volunteerId}
                      avatar={
                        vol.profile.avatarUrl ? (
                          <img
                            src={vol.profile.avatarUrl}
                            alt=''
                            style={{ width: 24, height: 24, borderRadius: '50%' }}
                          />
                        ) : (
                          <FallbackAvatar initials={getInitials(vol)} size={24} />
                        )
                      }
                      label={`${vol.profile.preferredName} ${vol.profile.lastName}`}
                      onDelete={() => handleRemoveVolunteer(vol.volunteerId)}
                      deleteIcon={<CloseIcon />}
                      variant='outlined'
                    />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box padding={2} display='flex' justifyContent='flex-end' width='100%'>
            <TabletButton variant='text' onClick={handleClose} style={{ marginRight: 8 }}>
              Cancel
            </TabletButton>
            <TabletButton
              variant='contained'
              color='primary'
              onClick={handleSubmit}
              disabled={!name.trim() || submitting}
            >
              {submitting ? <CircularProgress size={20} /> : 'Create Channel'}
            </TabletButton>
          </Box>
        </DialogActions>
      </Dialog>

      {showVolunteerDialog && (
        <SelectVolunteersDialog
          open={showVolunteerDialog}
          activity={{ activityId: '', name: '', program: { programId: '', name: '' } }}
          enableGroupByActivity={false}
          enableOrgGroup
          ignoredVolunteerIds={selectedVolunteers.map((v) => v.volunteerId)}
          handleClose={() => setShowVolunteerDialog(false)}
          handleAdd={handleAddVolunteers}
        />
      )}
    </>
  );
};
